import tinycolor from 'tinycolor2'

import { PRIMARY_PURPLE } from '../../colors'
import CircularProgress from '../../ui/CircularProgress'
import Tooltip from '../../ui/Tooltip'
import ArrowDownwardIcon from '../../ui/icons/ArrowDownward'
import ArrowUpwardIcon from '../../ui/icons/ArrowUpward'
import ContentCopyIcon from '../../ui/icons/ContentCopy'
import PauseIcon from '../../ui/icons/Pause'
import PlayArrowIcon from '../../ui/icons/PlayArrow'
import SettingsIcon from '../../ui/icons/Settings'
import UndoIcon from '../../ui/icons/Undo'

const ICON_BUTTON_SIZE = 24

const DEFAULT_COLOR = PRIMARY_PURPLE

const RowButton = ({
  onClick,
  disabled,
  children,
  color,
  'aria-label': ariaLabel,
}: {
  onClick?: () => void
  disabled?: boolean
  children: React.ReactNode
  color: string
  'aria-label'?: string
}) => {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className="flex h-full w-full grow cursor-pointer items-center justify-center self-stretch p-[2px] transition-[filter] hover:brightness-110 disabled:pointer-events-none disabled:opacity-60"
      style={{ backgroundColor: color }}
    >
      {children}
    </button>
  )
}

function PadInteractiveButtonsRow({
  skipback,
  play,
  pause,
  onSettingsPress,
  readyToPlay,
  playing,
  loading,
  fadeOutLastPressedAt,

  name,
  hasSoundFile,
  color = DEFAULT_COLOR,
  multishot,
  fadeOutOnPressDuration = 0,
  fadeInOnPressDuration = 0,
}: {
  skipback: () => void
  play: () => void
  pause: () => void
  onSettingsPress: () => void
  readyToPlay: boolean
  playing: boolean
  loading: boolean
  fadeOutLastPressedAt: number | null | undefined

  name: string
  hasSoundFile: boolean
  color: string
  multishot: boolean
  fadeOutOnPressDuration: number
  fadeInOnPressDuration: number
}) {
  const textColor = tinycolor
    .mostReadable(color, ['#333', '#fff'])
    .toHexString()

  const iconStyle = {
    color: textColor,
    height: ICON_BUTTON_SIZE,
    width: ICON_BUTTON_SIZE,
  }

  return (
    <div
      className="relative z-[1] flex w-full flex-row"
      style={{ backgroundColor: color }}
    >
      {!loading && readyToPlay && (
        <>
          <Tooltip title="Back to start">
            <div className="h-full min-w-0 flex-1">
              <RowButton aria-label="back" onClick={skipback} color={color}>
                <UndoIcon style={iconStyle} />
              </RowButton>
            </div>
          </Tooltip>

          {!playing && !fadeInOnPressDuration && (
            <Tooltip title="Play">
              <div className="h-full min-w-0 flex-1">
                <RowButton aria-label="play" onClick={play} color={color}>
                  <PlayArrowIcon style={iconStyle} />
                </RowButton>
              </div>
            </Tooltip>
          )}
          {!playing && Boolean(fadeInOnPressDuration) && (
            <Tooltip title={`Fade in (${fadeInOnPressDuration})`}>
              <div className="h-full min-w-0 flex-1">
                <RowButton
                  onClick={play}
                  disabled={!hasSoundFile}
                  color={color}
                >
                  <ArrowUpwardIcon style={iconStyle} />
                </RowButton>
              </div>
            </Tooltip>
          )}

          {/* no pause for multi-shot, use skipback */}
          {playing && !multishot && !fadeOutOnPressDuration && (
            <Tooltip title="Pause">
              <div className="h-full min-w-0 flex-1">
                <RowButton
                  onClick={pause}
                  disabled={!hasSoundFile}
                  color={color}
                >
                  <PauseIcon style={iconStyle} />
                </RowButton>
              </div>
            </Tooltip>
          )}
          {playing && !multishot && Boolean(fadeOutOnPressDuration) && (
            <Tooltip title={`Fade out (${fadeOutOnPressDuration}s)`}>
              <div className="h-full min-w-0 flex-1">
                <RowButton
                  onClick={pause}
                  disabled={Boolean(fadeOutLastPressedAt) || !hasSoundFile}
                  color={color}
                >
                  <ArrowDownwardIcon style={iconStyle} />
                </RowButton>
              </div>
            </Tooltip>
          )}

          {playing && multishot && (
            <Tooltip title="Multi-fire">
              <div className="h-full min-w-0 flex-1">
                <RowButton onClick={play} disabled={!name} color={color}>
                  <ContentCopyIcon style={iconStyle} />
                </RowButton>
              </div>
            </Tooltip>
          )}
        </>
      )}

      {loading && (
        <div className="h-full min-w-0 flex-1">
          <RowButton aria-label="Loading sound" disabled color={color}>
            <CircularProgress size={20} style={{ color: textColor }} />
          </RowButton>
        </div>
      )}

      <Tooltip title="Open soundpad settings">
        <div
          className={
            readyToPlay || loading ? 'h-full min-w-0 flex-1' : 'h-full w-full'
          }
        >
          <RowButton onClick={() => onSettingsPress()} color={color}>
            <SettingsIcon style={iconStyle} />
          </RowButton>
        </div>
      </Tooltip>
    </div>
  )
}

export default PadInteractiveButtonsRow
